import { AlertCircle, ArrowLeft } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Col,
  Container,
  Form,
  Row,
  Spinner,
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { CardActionsEditor } from '../../../components/CardActionsEditor';
import type { IconName } from '../../../components/IconSelector';
import IconSelector from '../../../components/IconSelector';
import type { CardAction } from '../../../service/api/types';
import { toCardActionPayload } from '../../../service/api/types';
import { useManagerCards } from '../../../store/managerCards';

export default function CreateCardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: cardId } = useParams<{ id: string }>();
  const isEditMode = !!cardId;

  const { cards, createCard, updateCard, error, clearError } =
    useManagerCards();

  const [formData, setFormData] = useState({
    title: '',
    icon: 'gift' as IconName | string,
    description: '',
    actions: [] as CardAction[],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showActionsEditor, setShowActionsEditor] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (isEditMode && cards.length > 0) {
      const card = cards.find(c => c._id === cardId);
      if (card) {
        setFormData({
          title: card.title || '',
          icon: card.icon || 'gift',
          description: card.description || '',
          actions: card.actions,
        });
      }
    }
  }, [isEditMode, cardId, cards]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(clearError, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, clearError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (formData.actions.length === 0) {
      setFormError(t('cards.atLeastOneActionRequired'));
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        title: formData.title,
        icon: formData.icon,
        description: formData.description,
        actions: toCardActionPayload(formData.actions),
      };

      if (isEditMode) {
        await updateCard(cardId!, payload);
      } else {
        await createCard(payload);
      }

      navigate('/manager/cards');
    } catch (err) {
      setFormError(
        err instanceof Error
          ? err.message
          : isEditMode
            ? t('cards.failedToUpdateCard')
            : t('cards.failedToCreateCard'),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const getActionsSummary = () => {
    if (formData.actions.length === 0) {
      return t('cards.actions.noActionsDefined');
    }

    return formData.actions
      .map(action => {
        if (action.type === 'sticker') {
          return action.amount > 0
            ? t('cards.actions.giveStickers', {
                count: Math.abs(action.amount),
              })
            : t('cards.actions.takeStickers', {
                count: Math.abs(action.amount),
              });
        } else if (action.type === 'item') {
          const itemName =
            typeof action.item === 'string'
              ? t('common.item')
              : action.item?.title || t('common.item');
          return t('cards.actions.giveItems', {
            count: action.amount,
            name: itemName,
          });
        } else {
          // effect action
          const effectLabel =
            action.effectType === 'set_price'
              ? t('effects.setPrice')
              : action.effectType === 'adjust_all_prices_percent'
                ? t('effects.adjustAllPrices')
                : t('effects.adjustItemPrice');
          return `⚡ ${effectLabel} (${action.value > 0 ? '+' : ''}${action.value}${action.effectType !== 'set_price' ? '%' : ''})`;
        }
      })
      .join(', ');
  };

  return (
    <div className="bg-white min-vh-100">
      <Container fluid>
        <Row className="align-items-center bg-white p-4 pb-2">
          <Col xs="auto">
            <Button
              variant="link"
              className="p-0 text-dark"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft size={24} />
            </Button>
          </Col>
          <Col>
            <h2 className="h5 fw-bold mb-0">
              {isEditMode
                ? t('cards.editCardPreset')
                : t('cards.createCardPreset')}
            </h2>
          </Col>
        </Row>

        <Form onSubmit={handleSubmit} className="p-4">
          {(error || formError) && (
            <Alert variant="danger" className="mb-4">
              <AlertCircle size={16} className="me-2" />
              {error || formError}
            </Alert>
          )}

          <Form.Group className="mb-3">
            <Form.Label>{t('cards.titleLabel')} *</Form.Label>
            <Form.Control
              type="text"
              value={formData.title}
              onChange={e =>
                setFormData({ ...formData, title: e.target.value })
              }
              required
              placeholder={t('cards.titlePlaceholder')}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{t('cards.iconLabel')}</Form.Label>
            <IconSelector
              value={formData.icon}
              onChange={(icon: IconName) => setFormData({ ...formData, icon })}
              className="w-100"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{t('cards.descriptionLabel')} *</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={formData.description}
              onChange={e =>
                setFormData({ ...formData, description: e.target.value })
              }
              required
              placeholder={t('cards.descriptionPlaceholder')}
            />
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label>{t('cards.actionsLabel')} *</Form.Label>
            <div className="border rounded p-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div
                    className={
                      formData.actions.length === 0
                        ? 'text-danger'
                        : 'text-muted'
                    }
                  >
                    {getActionsSummary()}
                  </div>
                </div>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setShowActionsEditor(true)}
                  type="button"
                >
                  {t('cards.editActions')}
                </Button>
              </div>
            </div>
          </Form.Group>

          <div className="d-flex gap-2">
            <Button
              variant="secondary"
              onClick={() => navigate(-1)}
              disabled={isSubmitting}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={
                isSubmitting ||
                !formData.title ||
                !formData.description ||
                formData.actions.length === 0
              }
              className="flex-grow-1"
            >
              {isSubmitting ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  {isEditMode ? t('cards.updating') : t('cards.creating')}
                </>
              ) : isEditMode ? (
                t('cards.updateCard')
              ) : (
                t('cards.createCard')
              )}
            </Button>
          </div>
        </Form>

        <CardActionsEditor
          show={showActionsEditor}
          onHide={() => setShowActionsEditor(false)}
          onSave={actions => setFormData({ ...formData, actions })}
          initialActions={formData.actions}
          title={
            isEditMode
              ? t('cards.editCardActions')
              : t('cards.defineCardActions')
          }
        />
      </Container>
    </div>
  );
}
